import {ReactNode} from "react";
import {useMediaQuery, Divider, VStack} from "@chakra-ui/react";
import {useNavigate} from "react-router-dom";
import {RequestItem} from "./RequestItem";
import {Request, statuses} from "../../models";
import {scrollbar} from "../styling";
import {EmptyState, InfoTooltip, L5Button, ListContainer, TopicIndicator} from "../UI";

type Req = Request & {newItems?: number}
type ListingProps = {
    requests: Req[]
    hasReachMax: boolean
    searchTerm?: string
    type: string
    onSelect: (r: Request) => void
    activeId?: string
    children?: ReactNode
};

const archivedStatuses: Request["status"][] = ["approved", "cancelled", "declined"];
const content :Record<string, {title: string, desc: string}> = {
    pending: {
        title: "No Pending Requests",
        desc: "Newly created requests will appear here.",
    },
    archived: {
        title: "No Archived Requests",
        desc: "Resolved or cancelled loads will be listed here for reference.",
    },
};

function AddRequest() {
    const navigate = useNavigate();
    return <L5Button
        type="primary"
        gap=".25em"
        onClick={() => navigate("/add-request")}
    >
        <svg width="24" height="24" aria-hidden="true">
            <use xlinkHref="#plus_circle" />
        </svg>
        <span>Add Request</span>
    </L5Button>;
}

function RequestList(props: ListingProps) {
    const [isLargeScreen] = useMediaQuery(`(min-width: 768px)`);

    function getContent(): {title: string, desc: string} {
        let res = Object.assign({}, content[props.type]);
        if (props.searchTerm) {
            res.title += ` matching "${props.searchTerm}"`;
            res.desc = "Check the spelling or try a different shipper, " +
            "carrier, pickup or dropoff location. You can also view all " +
            "loads by clearing the search input";
        }
        return res;
    }
    return <>
        {
            props.requests.length > 0 ? <ListContainer
                h="auto"
                p={{base: "16px", md: "20px"}}
                gap="12px"
                alignItems="stretch"
                flex="0 0 auto"
                maxW="min(425px, 100%)"
            >
                <VStack
                    flex="2 1 auto"
                    overflowY="auto"
                    position="relative"
                    sx={isLargeScreen ? scrollbar.styled : scrollbar.hidden}
                >
                    {props.children}
                    {props.requests.map(
                        (b) => <RequestItem
                            key={b.requestID}
                            {...b}
                            onClick={() => props.onSelect(b)}
                            borderColor={props.activeId === b.requestID ? "primary.500" : undefined}
                            hideIndicator={!archivedStatuses.includes(b.status)}
                        >
                            {!archivedStatuses.includes(b.status) && <>
                                <Divider color={props.activeId === b.requestID ? "primary.500" : undefined} h="1px"/>
                                <InfoTooltip
                                    content="Company association not established"
                                    fontSize="0.9em"
                                    icon="info"
                                    label="This request confirms the identity of the sender, but it does not verify whether they are associated with a company or organization."
                                    px="12px"
                                />
                                <InfoTooltip
                                    content="Permission not established"
                                    fontSize="0.9em"
                                    icon="info"
                                    label="This request confirms the identity of the sender, but it does not verify whether they have the necessary permissions to submit or authorize this request."
                                    px="12px"
                                />
                                <Divider color={props.activeId === b.requestID ? "primary.500" : undefined} h="1px"/>
                                <TopicIndicator
                                    bg="gray.100"
                                    color="gray.900"
                                    textAlign="center"
                                    content={statuses[b.status]}
                                    mx="12px"
                                />
                            </>}
                        </RequestItem>)}
                </VStack>
                <AddRequest/>
            </ListContainer> : <EmptyState h="auto" {...getContent()}>
                {props.type !== "archived" && <AddRequest/>}
            </EmptyState>
        }
    </>;
}

export default RequestList;
